import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import BillingSummary from '../components/BillingSummary';
import {
  createCheckoutSession,
  createPortalSession,
  fetchBillingPackages,
  getCachedBillingPackages,
  getBillingOfferPrice,
  getBillingOfferTitle,
  getPerCreditCopy,
  getSavingsCopy,
  isSubscriptionActive,
  splitBillingPackages
} from '../utils/billing';
import { pushHistoryBeforeExternalCheckout } from '../utils/appHistory';
import { BACKEND_URL } from '../utils/backendUrl';
import { getAuthHeader } from '../utils/auth';
import { trackEvent } from '../utils/telemetry';

const BillingPage = ({ onOpenPurchaseModal = null }) => {
  const { user, refreshUser } = useAuth();
  const [billingPackages, setBillingPackages] = useState([]);
  const [billingCountry, setBillingCountry] = useState('US');
  const [billingLoading, setBillingLoading] = useState(false);
  const [checkoutPackage, setCheckoutPackage] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [promoCode, setPromoCode] = useState(() => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const paramCode = urlParams.get('promo') || urlParams.get('ref');
      if (paramCode) return paramCode.toUpperCase();
      return sessionStorage.getItem('cmp_pending_promo') || '';
    } catch (_) {
      return '';
    }
  });
  const [promoStatus, setPromoStatus] = useState('idle');
  const [promoMessage, setPromoMessage] = useState('');
  const [referralData, setReferralData] = useState(null);
  const [copiedReferral, setCopiedReferral] = useState(false);

  useEffect(() => {
    if (!user) return;
    const fetchReferral = async () => {
      try {
        const authHeader = getAuthHeader();
        const res = await fetch(`${BACKEND_URL}/api/billing/my-referral`, {
          headers: { ...authHeader }
        });
        if (res.ok) {
          const data = await res.json();
          setReferralData(data);
        }
      } catch (_) {}
    };
    fetchReferral();
  }, [user]);

  const handleCopyReferral = () => {
    if (!referralData?.referral_link) return;
    navigator.clipboard?.writeText(referralData.referral_link);
    setCopiedReferral(true);
    trackEvent('referral_link_copied', { code: referralData.referral_code });
    setTimeout(() => setCopiedReferral(false), 2000);
  };

  const handleRedeemPromo = async (e) => {
    e?.preventDefault();
    if (!promoCode.trim()) return;
    setPromoStatus('loading');
    setPromoMessage('');
    try {
      const authHeader = getAuthHeader();
      const res = await fetch(`${BACKEND_URL}/api/billing/redeem-promo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader
        },
        body: JSON.stringify({ code: promoCode.trim() })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setPromoStatus('success');
        setPromoMessage(data.message || 'Promo code applied! Free credit added.');
        trackEvent('promo_code_redeemed', { code: data.code, source: 'billing_page' });
        setPromoCode('');
        if (typeof refreshUser === 'function') {
          refreshUser();
        }
      } else {
        setPromoStatus('error');
        setPromoMessage(data.detail || 'Could not apply promo code.');
      }
    } catch (err) {
      setPromoStatus('error');
      setPromoMessage('Network error. Please try again.');
    }
  };

  const subscriptionActive = isSubscriptionActive(user);
  const { creditPacks, subscriptions } = useMemo(
    () => splitBillingPackages(billingPackages),
    [billingPackages]
  );

  const getReturnTarget = () => {
    try {
      const rawOrigin = sessionStorage.getItem('craftmyparty_billing_origin');
      if (rawOrigin) {
        const origin = JSON.parse(rawOrigin);
        if (origin?.source === 'event_input') return origin;
      }
    } catch (_) {}

    return {
      source: 'billing',
      view: 'billing'
    };
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hasBillingReturnMarker = params.has('billing');
    const pendingStripeReturn = sessionStorage.getItem('craftmyparty_billing_return');
    if (!hasBillingReturnMarker && pendingStripeReturn) {
      // Stale sessionStorage key from a previous checkout attempt - clear it
      sessionStorage.removeItem('craftmyparty_billing_return');
    }

    let mounted = true;
    const loadBilling = async () => {
      const cached = getCachedBillingPackages('US');
      if (cached?.packages?.length) {
        setBillingPackages(cached.packages || []);
        setBillingCountry(cached.country || 'US');
        setBillingLoading(false);
      } else {
        setBillingLoading(true);
      }
      try {
        const data = await fetchBillingPackages('US', false);
        if (!mounted) return;
        setBillingPackages(data.packages || []);
        setBillingCountry(data.country || 'US');
      } catch (error) {
        if (mounted) setMessage({ type: 'error', text: error.message || 'Could not load billing options.' });
      } finally {
        if (mounted) setBillingLoading(false);
      }
    };
    loadBilling();
    return () => { mounted = false; };
  }, []);

  const startCheckout = async (packageId) => {
    setCheckoutPackage(packageId);
    setMessage({ type: '', text: '' });
    try {
      sessionStorage.setItem('craftmyparty_billing_return', JSON.stringify(getReturnTarget()));
      const origin = window.location.origin;
      const data = await createCheckoutSession({
        packageId,
        country: billingCountry || 'US',
        successUrl: `${origin}/?billing=success`,
        cancelUrl: `${origin}/?billing=cancelled`
      });
      pushHistoryBeforeExternalCheckout();
      window.location.assign(data.checkout_url);
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Payment initiation failed.' });
      setCheckoutPackage(null);
    }
  };

  const manageSubscription = async () => {
    try {
      const returnTarget = getReturnTarget();
      sessionStorage.setItem('craftmyparty_billing_return', JSON.stringify(returnTarget));
      const data = await createPortalSession(`${window.location.origin}/?billing=portal&return=billing`);
      pushHistoryBeforeExternalCheckout();
      window.location.assign(data.portal_url);
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Could not open subscription management.' });
    }
  };

  const renderBillingOffer = (pkg) => (
    <button
      key={pkg.id}
      type="button"
      onClick={() => startCheckout(pkg.id)}
      disabled={checkoutPackage === pkg.id || billingLoading}
      className="w-full rounded-2xl border border-gray-200 bg-white p-5 text-left transition hover:border-purple-300 hover:shadow-md disabled:cursor-wait disabled:opacity-70"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-extrabold text-gray-950">{getBillingOfferTitle(pkg)}</h3>
          <p className="mt-1 text-sm text-gray-500">{pkg.description}</p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-extrabold text-gray-950">{getBillingOfferPrice(pkg, billingCountry)}</div>
          {getPerCreditCopy(pkg, billingCountry) && (
            <div className="mt-1 text-xs font-medium text-gray-500">{getPerCreditCopy(pkg, billingCountry)}</div>
          )}
        </div>
      </div>
      {getSavingsCopy(pkg, billingCountry) && (
        <span className="mt-4 inline-flex rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700 ring-1 ring-emerald-100">
          {getSavingsCopy(pkg, billingCountry)}
        </span>
      )}
    </button>
  );

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="mb-8">
        <h1 className="text-2xl font-extrabold text-gray-900 mb-2">Billing & Credits</h1>
        <p className="text-gray-600">Purchase credit packs or manage your subscription</p>
      </div>

      {message.text && (
        <div className={`mb-6 p-4 rounded-xl border ${message.type === 'error' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-700'}`}>
          {message.text}
        </div>
      )}

      <div className="space-y-8">
        <BillingSummary
          user={user}
          onBuyCredits={onOpenPurchaseModal ? () => onOpenPurchaseModal({ tab: 'credits' }) : () => document.getElementById('billing-credit-packs')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          onViewPlans={onOpenPurchaseModal ? () => onOpenPurchaseModal({ tab: 'subscriptions' }) : () => document.getElementById('billing-subscriptions')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          onManageSubscription={manageSubscription}
        />

        {/* Promo Code Redemption Card */}
        <div className="rounded-2xl border border-gray-200 bg-gradient-to-r from-amber-50/60 to-rose-50/40 p-5 sm:p-6 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xl" role="img" aria-label="gift">🎁</span>
                <h3 className="text-base font-bold text-gray-900">Have a Promo or Referral Code?</h3>
              </div>
              <p className="mt-1 text-sm text-gray-600">
                Redeem a creator or launch invite code for instant free planning credits.
              </p>
            </div>
            <form onSubmit={handleRedeemPromo} className="flex flex-col sm:flex-row items-center gap-2 w-full sm:w-auto">
              <input
                type="text"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                placeholder="e.g. CELEBRATE1"
                disabled={promoStatus === 'loading'}
                className="w-full sm:w-48 uppercase rounded-xl border border-gray-300 bg-white px-3.5 py-2 text-sm font-semibold tracking-wider text-gray-900 placeholder:normal-case placeholder:font-normal placeholder:text-gray-400 focus:border-purple-600 focus:outline-none focus:ring-2 focus:ring-purple-200"
              />
              <button
                type="submit"
                disabled={!promoCode.trim() || promoStatus === 'loading'}
                className="w-full sm:w-auto rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 px-5 py-2 text-sm font-bold text-white shadow hover:opacity-95 disabled:opacity-50 transition"
              >
                {promoStatus === 'loading' ? 'Applying...' : 'Apply Code'}
              </button>
            </form>
          </div>
          {promoMessage && (
            <div className={`mt-3 text-xs font-semibold px-3 py-2 rounded-lg ${promoStatus === 'success' ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}>
              {promoMessage}
            </div>
          )}
        </div>

        {/* Give 1, Get 1 Referral Program Card */}
        <div className="rounded-2xl border border-purple-200 bg-gradient-to-r from-purple-50 via-pink-50/50 to-indigo-50/60 p-5 sm:p-6 shadow-sm">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xl" role="img" aria-label="sparkles">✨</span>
                <h3 className="text-base font-bold text-gray-900">Give a Free Plan, Get a Free Plan</h3>
                <span className="rounded-full bg-purple-100 px-2.5 py-0.5 text-[11px] font-bold text-purple-700">
                  Referral Program
                </span>
              </div>
              <p className="mt-1 text-sm text-gray-600 max-w-xl">
                Share your unique invite link with fellow hosts. When they use your code, they get 1 free planning credit — and you earn 1 bonus credit!
              </p>
              {referralData && (
                <div className="mt-3 flex items-center gap-4 text-xs font-semibold text-purple-900">
                  <span className="bg-white/80 px-2.5 py-1 rounded-lg border border-purple-100">
                    Friends Referred: <strong>{referralData.referrals_count || 0}</strong>
                  </span>
                  <span className="bg-white/80 px-2.5 py-1 rounded-lg border border-purple-100">
                    Credits Earned: <strong>{referralData.credits_earned || 0}</strong>
                  </span>
                </div>
              )}
            </div>

            {referralData?.referral_link ? (
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full lg:w-auto">
                <div className="flex items-center rounded-xl border border-purple-200 bg-white px-3 py-2 text-xs font-mono text-gray-700 select-all overflow-hidden max-w-xs truncate shadow-inner">
                  {referralData.referral_link}
                </div>
                <button
                  type="button"
                  onClick={handleCopyReferral}
                  className="rounded-xl bg-purple-700 px-5 py-2 text-xs font-bold text-white shadow hover:bg-purple-800 transition whitespace-nowrap"
                >
                  {copiedReferral ? '✓ Copied Link!' : 'Copy Invite Link'}
                </button>
              </div>
            ) : (
              <div className="text-xs text-purple-600 italic">Generating your personal referral link...</div>
            )}
          </div>
        </div>

        {!onOpenPurchaseModal && (
          <>
            <div id="billing-credit-packs">
              <h2 className="mb-4 text-xl font-bold text-gray-900">Credit Packs</h2>
              <div className="grid gap-4 sm:grid-cols-1">
                {billingLoading ? (
                  <div className="rounded-2xl border border-gray-200 bg-gray-50 p-8 text-center text-gray-500">Loading options...</div>
                ) : (
                  creditPacks.map(renderBillingOffer)
                )}
              </div>
            </div>

            <div id="billing-subscriptions">
              <h2 className="mb-4 text-xl font-bold text-gray-900">Subscriptions</h2>
              {subscriptionActive ? (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6">
                  <h3 className="text-lg font-bold text-emerald-900">Your subscription is active</h3>
                  <p className="mt-2 text-emerald-800">Buy credit packs anytime if you need more credits before the next monthly allowance arrives.</p>
                  <button
                    type="button"
                    onClick={manageSubscription}
                    className="mt-6 rounded-xl bg-emerald-700 px-6 py-2.5 text-sm font-bold text-white hover:bg-emerald-800 transition shadow-sm"
                  >
                    Manage or cancel subscription
                  </button>
                </div>
              ) : (
                <div className="grid gap-4 sm:grid-cols-1">
                  {billingLoading ? (
                    <div className="rounded-2xl border border-gray-200 bg-gray-50 p-8 text-center text-gray-500">Loading options...</div>
                  ) : (
                    subscriptions.map(renderBillingOffer)
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BillingPage;
